import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Board } from '../types/common';
import { boardsApi } from '../api/endpoints/boardsApi';
import { CreateBoardRequest, UpdateBoardRequest } from '../api/types/BoardTypes';

interface BoardState {
  boards: Board[];
  currentBoard: Board | null;
  loading: boolean;
  error: string | null;
}

const initialState: BoardState = {
  boards: [],
  currentBoard: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchBoards = createAsyncThunk('boards/fetchBoards', async () => {
  const response = await boardsApi.getBoards();
  return response.boards;
});

export const fetchBoard = createAsyncThunk('boards/fetchBoard', async (boardId: string) => {
  const response = await boardsApi.getBoard(boardId);
  return response.board;
});

export const createBoard = createAsyncThunk(
  'boards/createBoard',
  async (data: CreateBoardRequest) => {
    const response = await boardsApi.createBoard(data);
    return response.board;
  }
);

export const updateBoard = createAsyncThunk(
  'boards/updateBoard',
  async ({ boardId, data }: { boardId: string; data: UpdateBoardRequest }) => {
    const response = await boardsApi.updateBoard(boardId, data);
    return response.board;
  }
);

export const deleteBoard = createAsyncThunk('boards/deleteBoard', async (boardId: string) => {
  await boardsApi.deleteBoard(boardId);
  return boardId;
});

const boardSlice = createSlice({
  name: 'boards',
  initialState,
  reducers: {
    clearCurrentBoard: (state) => {
      state.currentBoard = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch boards
    builder
      .addCase(fetchBoards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBoards.fulfilled, (state, action: PayloadAction<Board[]>) => {
        state.loading = false;
        state.boards = action.payload;
        // Deliberately does NOT auto-pick action.payload[0] into
        // currentBoard here. Board-agnostic entry points (bare
        // /sprint-board, /sprint-dashboard, reached via the Favorites
        // sidebar link with no board id in the URL) dispatch fetchBoards()
        // specifically to let the user choose - see the "Select a board"
        // picker in SprintBoard.tsx/SprintDashboard.tsx, which only renders
        // while currentBoard stays unset. Auto-selecting an arbitrary first
        // board here used to silently skip that picker and render whichever
        // board happened to come back first from the API - indistinguishable
        // from real cross-board data leakage to a user with multiple boards.
      })
      .addCase(fetchBoards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch boards';
      });

    // Fetch single board
    builder
      .addCase(fetchBoard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBoard.fulfilled, (state, action: PayloadAction<Board>) => {
        state.loading = false;
        state.currentBoard = action.payload;
      })
      .addCase(fetchBoard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch board';
      });

    // Create board
    builder
      .addCase(createBoard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBoard.fulfilled, (state, action: PayloadAction<Board>) => {
        state.loading = false;
        state.boards = state.boards || [];
        state.boards.push(action.payload);
      })
      .addCase(createBoard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create board';
      });

    // Update board
    builder
      .addCase(updateBoard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBoard.fulfilled, (state, action: PayloadAction<Board>) => {
        state.loading = false;
        const index = state.boards.findIndex((board) => board.id === action.payload.id);
        if (index !== -1) {
          state.boards[index] = action.payload;
        }
        if (state.currentBoard?.id === action.payload.id) {
          state.currentBoard = action.payload;
        }
      })
      .addCase(updateBoard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update board';
      });

    // Delete board
    builder
      .addCase(deleteBoard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteBoard.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.boards = state.boards.filter((board) => board.id !== action.payload);
        if (state.currentBoard?.id === action.payload) {
          state.currentBoard = null;
        }
      })
      .addCase(deleteBoard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete board';
      });
  },
});

export const { clearCurrentBoard, clearError } = boardSlice.actions;
export default boardSlice.reducer;
